import {deleteAllIds} from "./helper/deleteAllIds";
import {getInitData} from "./helper/getInitData";
import {RowType} from "../../../components/Editable/FormItem/Rows/RowType";
import {select} from "../../../shared/SagaService";
import _ from 'lodash';
import { processFormItems } from "./helper/processFormItems";
import { showConfirmationModalPromise } from "./helper/Modal/showConfirmationModalPromise";
import { READ_PRODUCT_DETAIL_LIST_LIMIT, getProductDetailList } from "../../List/productDetaiListHelper";
import { readAutosaveStorage } from "../../LocalStorage/helpers/AutosaveTransactions/readStorage";
import { showWillOverrideAutosaveDataPromise } from "./helper/Modal/showWillOverrideAutosaveDataPromise";
import { formatNumber } from "../../../components/FormatNumber";

// Case 6: Copy Transaction
// - Different category
// - Supported:
//      - Invoice => Bill
//      - Invoice => GRN
//      - Invoice => Purchase Order
//      - Order => Purchase Order
export function* copyItems(transactionType, originType, originTransaction = {}) {
    // Check if there's any autosaved data for the transaction type, and prompt modal if there is
    const { data, last_saved } = yield readAutosaveStorage(transactionType) || {};

    if (data) {
        const autosaveModalRef = yield select(state => state.Transaction.autosaveModalRef);
        const isProceed = yield showWillOverrideAutosaveDataPromise(transactionType, autosaveModalRef, last_saved, 'copy items');
        if (!isProceed)
            return {isProceed: false, transaction: null};
    }

    // Init Data
    const initData = yield getInitData(transactionType);

    // List API: ProductList
    const productList = yield select(state => state.List.productList);
    const settings = yield select(state => state.List.settings);
    const inventorySystem = yield select(state => state.Auth.company?.inventory_system);

    // List API: Product
    // const products = {}; // Collecting results from List API: Product
    // function* getProduct(productId) {
    //     if (products[productId])
    //         return products[productId];

    //     yield readLists([L.product_detail], {
    //         [L.product_detail]: {
    //             product_id: productId,
    //             type: 'PURCHASE',
    //             date: Moment().format("YYYY-MM-DD"),
    //         },
    //     });

    //     products[productId] = yield select(state => state.List.productDetail);
    //     return products[productId];
    // }

    // Function returning boolean, using ProductList
    function filterItem(item) {
        if (!item.product_id)
            return false;

        if (item.type === RowType.bundle && item.children?.length === 0)
            return false;

        const product = productList.find(p => p.id === item.product_id);
        if (!product)
            return false;

        return product.is_selling && product.is_buying;
    }

    /**
     * Get all product ids from a form items - iteratively for bundle
     * @param {*} arr
     * @param {*} allIds
     * @returns {Number[]}
     */
    function getAllProductIds(arr, allIds = []) {
        // process items from results
        for (let i = 0; i < arr.length; i += 1) {
            const item = arr[i];

            switch (item.type) {
                case RowType.bundle: {
                    if (!filterItem(item))
                        continue;

                    const children = getAllProductIds(item.children, allIds);

                    if (children.length === 0)
                        continue;

                    // skip repeated product ids
                    if (!allIds.includes(item.product_id))
                        allIds.push(item.product_id);
                    break;
                }
                case RowType.bundle_item:
                default:
                    if (!filterItem(item))
                        continue;

                    // skip repeated product ids
                    if (!allIds.includes(item.product_id))
                        allIds.push(item.product_id);
                    break;
            }
        }
        return allIds;
    }

    /**
     * Previously called iterateArray(arr), to preprocess items (unit price)
     * Now, api calls are done in batches of 50 items instead of every item
     * @param {Object[]} arr
     * @param {Object[]} productDetailList - result from readLists
     * @param {int} parentId - bundle items' parent id
     * @returns
     */
    function* preprocessItems(arr, productDetailList, parentId) {
        const filteredArray = [];

        // process items from results
        for (let i = 0; i < arr.length; i += 1) {
            const item = arr[i];

            let product;
            // if is bundle discount row, look at the parent to find the discount amount
            if (item.type === RowType.bundle_discount && parentId)
                product = productDetailList.find(detail => detail.id === parentId);
            else
                product = productDetailList.find(detail => detail.id === item.product_id);

            switch (item.type) {
                case RowType.bundle: {
                    if (!filterItem(item))
                        continue;

                    const children = yield preprocessItems(item.children, productDetailList, item.product_id);

                    if (children.length === 0)
                        continue;

                    filteredArray.push({
                        ...item,
                        // use purchase’s description or product name.
                        unit_price: null,
                        description: product.name + (product.description ? `\n${product.description}` : ''),
                        children,
                    });
                    break;
                }
                case RowType.bundle_discount: {
                    filteredArray.push({
                        ...item,
                        discount: formatNumber(item.discount, 2),
                        tax_code_id: null, // on copy items sale -> purchase, tax code for bundle discount will not be copied
                    });
                    break;
                }
                case RowType.bundle_item:
                default: {
                    if (!filterItem(item))
                        continue;

                    // const uom = hasUOMSubscription ?
                    //     product.units.find(u => item.product_unit_id === u.id) :
                    //     product.units.find(u => product.base_unit_id === u.id);

                    // if (!hasUOMSubscription && item.product_unit_id !== product.base_unit_id)
                    //     noUOMSubWarning = true;

                    filteredArray.push({
                        ...item,
                        // account: use purchase / inventory account.
                        account_id: (
                            (product.track_inventory && inventorySystem === 'perpetual') ?
                                product.inventory_account_id :
                                product.account_id
                        ),
                        // account_name: "Sales Income",
                        // account_code: "5000",
                        // description: use purchase’s description or product name.
                        description: product.name + (product.description ? `\n${product.description}` : ''),
                        product_unit_id: item.product_unit_id,
                        unit_price: product.units.find(u => item.product_unit_id === u.id)?.unit_price,
                        // use purchase's tax code
                        tax_code_id: product.tax_code_id,
                    });
                }
            }
        }
        return filteredArray;
    }

    const originFormItems = originTransaction.form_items || [];

    const productsIds = getAllProductIds(originFormItems, []);

    // chunk product ids into batch of 50
    const productIdsChunks = _.chunk(productsIds, READ_PRODUCT_DETAIL_LIST_LIMIT);

    // read each chunk of 50 ids sequentially
    let productDetailList = [];
    for (let i = 0; i < productIdsChunks.length; i += 1) {
        const idsChunk = productIdsChunks[i];
        const productsDetail = yield getProductDetailList(idsChunk, 'PURCHASE');
        productDetailList.push(productsDetail);
    }
    productDetailList = productDetailList.flat(); // [[], [], []] -> []
    // console.log('aaa batchDispatch', {originFormItems, productIdsChunks, productDetailList});

    // Process FormItems - round 1
    const formItems = yield preprocessItems(originFormItems, productDetailList);
    const transaction = {
        ...initData,
        // form_items: yield iterateArray(originFormItems),
        form_items: formItems,
    };

    // Process FormItems - round 2
    const {
        // processedFormItems,
        finalFormItems,
        nonBaseUomItems,
        hasTaxChange,
        hasLocationChange,
        removedBundles,
    } = processFormItems({
        formItems,
        productList,
        settings,
    });

    // REMOVE non-base uom items
    if (nonBaseUomItems?.length || removedBundles?.length ||
        hasLocationChange || hasTaxChange
    ) {
        // yield to make the Modal.confirm synchronous by using Promise
        yield showConfirmationModalPromise(
            nonBaseUomItems,
            removedBundles,
            {
                hasLocationChange, hasTaxChange,
            })
            .then(() => {
                // User clicked OK - remove invalid form items with non-base UOM
                transaction.form_items = finalFormItems;
            });
    }
    else {
        transaction.form_items = finalFormItems;
    }

    // Form Items
    // if (transaction.form_items) {
    //     transaction.form_items = transaction.form_items.map(formItem => {
    //         // Ignore subtitle and subtotal lines
    //         if ([RowType.subtotal, RowType.subtitle].includes(formItem.type))
    //             return formItem;

    //         // Remove transfer transaction information
    //         return {
    //             ...formItem,
    //             transfer_transaction: undefined,
    //             transfer_item_id: undefined,
    //             balance_quantity: undefined,
    //             transferred_to_quantity: undefined,
    //         };
    //     });
    // }
    // Remove all ids
    return {
        isProceed: true,
        transaction: deleteAllIds(transaction),
    };
}

// ------------------------------------ **** DEPRECATED **** ------------------------------------
// ---------- DEPRECATED: using L.product iterate one by one to get product detail -----------
// ------------------------------------ **** DEPRECATED **** ------------------------------------
// import {deleteAllIds} from "./helper/deleteAllIds";
// import {getInitData} from "./helper/getInitData";
// import {RowType} from "../../../components/Editable/FormItem/Rows/RowType";
// import {select} from "../../../shared/SagaService";
// import {readLists} from "./helper/readLists";
// import {L} from "../../List/reducer";
// import {SubscriptionCheck} from "../../../components/Access/helper/subscription/SubscriptionCheck";
// import {SubscriptionScopeKey} from "../../../components/Access/helper/subscription/enum/SubscriptionScopeKey";
// import Moment from "moment";

// // Case 6: Copy Transaction
// // - Different category
// // - Supported:
// //      - Invoice => Bill
// //      - Invoice => GRN
// //      - Invoice => Purchase Order
// export function* copyItems(transactionType, originType, originTransaction = {}) {
//     // Init Data
//     const initData = yield getInitData(transactionType);

//     // List API: ProductList
//     const productList = yield select(state => state.List.productList);
//     const settings = yield select(state => state.List.settings);
//     const inventorySystem = yield select(state => state.Auth.company?.inventory_system);

//     const hasTaxSubscription = SubscriptionCheck.withKeys(SubscriptionScopeKey.['TAX.SST']).hasSubscriptionTo();

//     // List API: Product
//     const products = {}; // Collecting results from List API: Product
//     function* getProduct(productId) {
//         console.log('aaa copyItems - getProduct 0', {products, productId, return: products[productId]});

//         if (products[productId])
//             return products[productId];

//         yield readLists([L.product_detail], {
//             product_detail: {
//                 product_id: productId,
//                 type: 'PURCHASE',
//                 date: Moment().format("YYYY-MM-DD"),
//             },
//         });
//         console.log('aaa copyItems - getProduct 1 - readLists');

//         products[productId] = yield select(state => state.List.productDetail);
//         return products[productId];
//     }

//     // Function returning boolean, using ProductList
//     function filterItem(item) {
//         console.log('aaa copyItems - filterItem 0', {item});

//         if (!item.product_id)
//             return false;

//         if (item.type === RowType.bundle && item.children?.length === 0)
//             return false;

//         const product = productList.find(p => p.id === item.product_id);
//         console.log('aaa copyItems - filterItem 1', {product, item});
//         if (!product)
//             return false;

//         return product.is_selling && product.is_buying;
//     }

//     // Iterate Function
//     function* iterateArray(arr) {
//         const filteredArray = [];
//         for (let i = 0; i < arr.length; i += 1) {
//             const item = arr[i];
//             console.log('aaa copyItems - iterateArray 0', {i, item});

//             switch (item.type) {
//                 case RowType.bundle: {
//                     const children = yield iterateArray(item.children);

//                     if (children.length === 0)
//                         continue;

//                     if (!filterItem(item))
//                         continue;

//                     const product = yield getProduct(item.product_id);

//                     filteredArray.push({
//                         ...item,
//                         // use purchase’s description or product name.
//                         unit_price: null,
//                         description: product.name + (product.description ? `\n${product.description}` : ''),
//                         children,
//                     });
//                     break;
//                 }
//                 case RowType.bundle_item:
//                 default: {
//                     if (!filterItem(item))
//                         continue;

//                     const product = yield getProduct(item.product_id);
//                     console.log('aaa copyItems - iterateArray 0-default-1', {i, item, product});

//                     filteredArray.push({
//                         ...item,
//                         // account: use purchase / inventory account.
//                         account_id: (
//                             (product.track_inventory && inventorySystem === 'perpetual') ?
//                                 product.inventory_account_id :
//                                 product.account_id
//                         ),
//                         // account_name: "Sales Income",
//                         // account_code: "5000",
//                         // description: use purchase’s description or product name.
//                         description: product.name + (product.description ? `\n${product.description}` : ''),
//                         // unit_price: default purchase price for the uom.
//                         unit_price: product.units.find(u => u.unit_price)?.unit_price,
//                         product_unit_id: product.default_unit_id,
//                         // tax: use the product default tax / default purchase tax setting.
//                         tax_code_id: hasTaxSubscription ? (
//                             product.tax_code_id || settings.purchase_tax_code_id
//                         ) : undefined,
//                     });
//                 }
//             }
//         }
//         return filteredArray;
//     }

//     const originFormItems = originTransaction.form_items || [];

//     console.log('aaa copyItems - 1', {productList, settings, inventorySystem, hasTaxSubscription, originFormItems});
//     const parsedFormItems = yield iterateArray(originFormItems);
//     console.log('aaa copyItems - 2', {parsedFormItems});
//     const transaction = {
//         ...initData,
//         form_items: parsedFormItems,
//     };

//     // Form Items
//     if (transaction.form_items) {
//         transaction.form_items = transaction.form_items.map(formItem => {
//             // Ignore subtitle and subtotal lines
//             if ([RowType.subtotal, RowType.subtitle].includes(formItem.type))
//                 return formItem;

//             // Remove transfer transaction information
//             return {
//                 ...formItem,
//                 transfer_transaction: undefined,
//                 transfer_item_id: undefined,
//                 balance_quantity: undefined,
//                 transferred_to_quantity: undefined,
//             };
//         });
//     }
//     // Remove all ids
//     return deleteAllIds(transaction);
// }
